/** Field map: the agency's basemap (Esri when set up) with a Layers tab to switch things on and off. */
import { useEffect, useMemo, useState } from 'react';
import { BOARD_KINDS, type BoardKind } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { SlippyMap, type MapMarker, type MapVector } from '../shared/SlippyMap.tsx';
import { ErrorBox, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at } from './common.tsx';

type Built = 'units' | 'boards' | 'crimes';
const BUILT: { id: Built; label: string; color: string }[] = [
  { id: 'units', label: 'Deputies on duty', color: '#1f63e6' },
  { id: 'boards', label: 'Command boards', color: '#c0262d' },
  { id: 'crimes', label: 'Crimes, last 14 days', color: '#7a4fd6' },
];
const KEY = 'bhq.fieldmap.layers';
const loadOn = (): Record<string, boolean> => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}');
  } catch {
    return {};
  }
};

/** GeoJSON geometry → markers (points) and vectors (lines, areas). */
function toShapes(layer: any): { markers: MapMarker[]; vectors: MapVector[] } {
  const markers: MapMarker[] = [];
  const vectors: MapVector[] = [];
  const ring = (c: number[][]) => c.map(([lng, lat]) => ({ lat: lat!, lng: lng! }));
  layer.features.forEach((f: any, i: number) => {
    const g = f.geometry;
    if (!g) return;
    const label = f.properties?.label ?? layer.name;
    if (g.type === 'Point') markers.push({ id: `${layer.id}-${i}`, lat: g.coordinates[1], lng: g.coordinates[0], color: layer.color, title: label, dot: true });
    if (g.type === 'MultiPoint') g.coordinates.forEach((c: number[], k: number) => markers.push({ id: `${layer.id}-${i}-${k}`, lat: c[1]!, lng: c[0]!, color: layer.color, title: label, dot: true }));
    if (g.type === 'LineString') vectors.push({ points: ring(g.coordinates), color: layer.color });
    if (g.type === 'MultiLineString') g.coordinates.forEach((c: number[][]) => vectors.push({ points: ring(c), color: layer.color }));
    if (g.type === 'Polygon') vectors.push({ points: ring(g.coordinates[0] ?? []), color: layer.color, area: true });
    if (g.type === 'MultiPolygon') g.coordinates.forEach((p: number[][][]) => vectors.push({ points: ring(p[0] ?? []), color: layer.color, area: true }));
  });
  return { markers, vectors };
}

function AddLayer({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ name: '', url: '', color: '#e2741d' });
  const a = useAction();
  return (
    <div className="pm-add stack sm">
      <label>
        Name
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Patrol zones" />
      </label>
      <label>
        Web address
        <input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://…/FeatureServer/0 or a .geojson link" />
      </label>
      <label className="row gap-sm">
        Color <input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} aria-label="Layer color" />
      </label>
      <ErrorBox error={a.error} />
      <button className="btn sm primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/map-layers', f), setF({ name: '', url: '', color: '#e2741d' }), onDone()))}>
        Add layer
      </button>
    </div>
  );
}

export function FieldMapPage() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/field-map'));
  const [on, setOn] = useState<Record<string, boolean>>(() => ({ units: true, boards: true, crimes: false, ...loadOn() }));
  const [tab, setTab] = useState<'layers' | null>('layers');
  const [adding, setAdding] = useState(false);
  const [layerData, setLayerData] = useState<Record<string, any>>({});
  const [layerErr, setLayerErr] = useState<Record<string, string>>({});
  const a = useAction();
  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(on));
    } catch {
      /* fine */
    }
  }, [on]);
  // Keep positions fresh.
  useEffect(() => {
    const t = setInterval(() => r.reload(), 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Fetch agency layers when switched on.
  useEffect(() => {
    for (const l of r.data?.layers ?? [])
      if (on[`l:${l.id}`] && !layerData[l.id] && !layerErr[l.id])
        api
          .get<any>(`/api/map-layers/${l.id}/data`)
          .then((d) => setLayerData((x) => ({ ...x, [l.id]: d })))
          .catch((e) => setLayerErr((x) => ({ ...x, [l.id]: e?.message ?? 'Couldn’t load' })));
  }, [r.data, on, layerData, layerErr]);
  const { markers, vectors } = useMemo(() => {
    const m: MapMarker[] = [];
    const v: MapVector[] = [];
    const d = r.data;
    if (!d) return { markers: m, vectors: v };
    for (const l of d.layers) if (on[`l:${l.id}`] && layerData[l.id]) {
      const s = toShapes(layerData[l.id]);
      m.push(...s.markers);
      v.push(...s.vectors);
    }
    if (on.crimes) for (const c of d.crimes ?? []) m.push({ id: `c:${c.id}`, lat: c.lat, lng: c.lng, color: '#7a4fd6', title: `${c.case_number} · ${c.title ?? c.primary_code ?? 'Report'}`, dot: true });
    if (on.units) for (const u of d.units ?? []) m.push({ id: `u:${u.id}`, lat: u.lat, lng: u.lng, color: '#1f63e6', label: u.callNumber ?? undefined, title: `${u.name}${u.callNumber ? ` (${u.callNumber})` : ''}${u.car ? ` · car ${u.car}` : ''}` });
    if (on.boards) for (const b of d.boards) m.push({ id: `b:${b.id}`, lat: b.lat, lng: b.lng, color: '#c0262d', label: '!', title: `${b.number} · ${BOARD_KINDS[b.kind as BoardKind] ?? b.kind} · ${b.location}` });
    return { markers: m, vectors: v };
  }, [r.data, on, layerData]);
  const click = (id: string) => {
    if (id.startsWith('b:')) navigate(at(`/command/${id.slice(2)}`));
    else if (id.startsWith('c:')) navigate(at(`/reports/${id.slice(2)}`));
  };
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const d = r.data;
  return (
    <div className="pm">
      <div className="pm-map">
        <SlippyMap height="100%" markers={markers} vectors={vectors} fitKey={`${on.units}|${on.boards}|${on.crimes}|${Object.keys(layerData).length}`} ariaLabel="Field map" onMarkerClick={click} />
      </div>
      <div className={`pm-panel${tab ? ' open' : ''}`}>
        <div className="pm-tabs" role="tablist" aria-label="Map panels">
          <button type="button" role="tab" aria-selected={tab === 'layers'} className={tab === 'layers' ? 'on' : ''} onClick={() => setTab(tab === 'layers' ? null : 'layers')}>
            Layers
          </button>
        </div>
        {tab === 'layers' && (
          <div className="pm-body" role="tabpanel" aria-label="Layers">
            <h3>BlueHQ</h3>
            {BUILT.filter((b) => b.id === 'boards' || d[b.id] != null).map((b) => (
              <label key={b.id} className="check pm-layer">
                <input type="checkbox" checked={!!on[b.id]} onChange={(e) => setOn({ ...on, [b.id]: e.target.checked })} />
                <i style={{ background: b.color }} aria-hidden="true" /> {b.label}
                <span className="small muted">{b.id === 'units' ? d.units.length : b.id === 'boards' ? d.boards.length : d.crimes.length}</span>
              </label>
            ))}
            <h3>Agency layers</h3>
            {!d.layers.length && <p className="small muted">None yet.{d.canLayers ? ' Add your GIS layers (zones, beats, schools…) below.' : ' An administrator can add them.'}</p>}
            {d.layers.map((l: any) => (
              <div key={l.id} className="pm-layer-row">
                <label className="check pm-layer">
                  <input type="checkbox" checked={!!on[`l:${l.id}`]} onChange={(e) => setOn({ ...on, [`l:${l.id}`]: e.target.checked })} />
                  <i style={{ background: l.color }} aria-hidden="true" /> {l.name}
                </label>
                {layerErr[l.id] && <div className="small bad-text">{layerErr[l.id]}</div>}
                {d.canLayers && (
                  <button type="button" className="btn link small" disabled={a.busy} onClick={() => window.confirm(`Remove the ${l.name} layer?`) && a.run(async () => (await api.post(`/api/map-layers/${l.id}/delete`), r.reload()))}>
                    Remove
                  </button>
                )}
              </div>
            ))}
            {d.canLayers &&
              (adding ? (
                <AddLayer onDone={() => (setAdding(false), r.reload())} />
              ) : (
                <button type="button" className="btn sm" onClick={() => setAdding(true)}>
                  + Add a layer
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
